import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { User, Module } from '../models/user.model';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly tokenStorageKey = 'access_token';
    private readonly activeWorkspaceStorageKey = 'active_workspace_id';
    private currentUserSubject = new BehaviorSubject<User | null>(null);
    public currentUser$ = this.currentUserSubject.asObservable();
    private initializedSubject = new BehaviorSubject<boolean>(false);
    public initialized$ = this.initializedSubject.asObservable();
    private activeWorkspaceSubject = new BehaviorSubject<string | null>(localStorage.getItem(this.activeWorkspaceStorageKey));
    public activeWorkspace$ = this.activeWorkspaceSubject.asObservable();
    private apiUrl = `${environment.apiUrl}/auth`;
    private profileUrl = `${environment.apiUrl}/profile`;

    constructor(private http: HttpClient) {
        this.loadCurrentUser();
    }

    loadCurrentUser() {
        const token = localStorage.getItem(this.tokenStorageKey);
        if (token) {
            this.http.get<User>(`${this.profileUrl}/me`).subscribe({
                next: (user) => {
                    this.currentUserSubject.next(user);
                    this.initializedSubject.next(true);
                },
                error: () => {
                    this.logout();
                    this.initializedSubject.next(true);
                }
            });
            return;
        }

        this.currentUserSubject.next(null);
        this.initializedSubject.next(true);
    }

    login(username: string, password: string): Observable<any> {
        return this.http.post<{ access_token: string }>(`${this.apiUrl}/login`, { username, password })
            .pipe(tap(res => {
                localStorage.setItem(this.tokenStorageKey, res.access_token);
                this.clearWorkspaceSelection();
                this.loadCurrentUser();
            }));
    }

    registerCompany(payload: { name: string; username: string; email: string; password: string }): Observable<any> {
        return this.http.post<{ access_token: string }>(`${this.apiUrl}/register-company`, payload)
            .pipe(tap(res => {
                localStorage.setItem(this.tokenStorageKey, res.access_token);
                this.clearWorkspaceSelection();
                this.loadCurrentUser();
            }));
    }

    logout() {
        localStorage.removeItem(this.tokenStorageKey);
        this.clearWorkspaceSelection();
        this.currentUserSubject.next(null);
    }

    getCurrentUser(): User | null {
        return this.currentUserSubject.value;
    }

    isAuthenticated(): boolean {
        return !!this.currentUserSubject.value;
    }

    isSuperAdmin(): boolean {
        return this.currentUserSubject.value?.role === 'SUPER_ADMIN';
    }

    hasModule(module: Module): boolean {
        const user = this.currentUserSubject.value;
        if (!user) return false;
        if (user.role === 'SUPER_ADMIN') return true;
        return user.enabledModules.includes(module);
    }

    getActiveWorkspaceId(): string | null {
        return this.activeWorkspaceSubject.value;
    }

    switchWorkspace(accountId: string | null) {
        if (accountId) {
            localStorage.setItem(this.activeWorkspaceStorageKey, accountId);
            this.activeWorkspaceSubject.next(accountId);
        } else {
            this.clearWorkspaceSelection();
        }

        this.loadCurrentUser();
    }

    private clearWorkspaceSelection() {
        localStorage.removeItem(this.activeWorkspaceStorageKey);
        this.activeWorkspaceSubject.next(null);
    }
}
